import { v, type Infer } from "convex/values";
import type { Doc } from "../_generated/dataModel";
import { vFetchedLyrics } from "./lyricsSource";
import { normalisedSongKey, typedIdentity } from "./songs";

/** A fingerprint match below this is treated as no match at all. */
export const FINGERPRINT_ACCEPT = 0.9;
/** A recording found by searching the typed text never outranks a fingerprint. */
const MBID_CAP = 0.85;
/** The typed title and artist after the language model cleaned them; also
 * what a service's own metadata is worth when an import makes a song. */
export const TEXT_CONFIDENCE = 0.5;
export const MANUAL_CONFIDENCE = 1;

/** How the web app names each way a song was identified. */
export const IDENTIFIED_BY_LABEL = {
  fingerprint: "fingerprint",
  mbid: "text match",
  text: "typed",
  manual: "corrected",
} satisfies Record<Doc<"songs">["identifiedBy"], string>;

/** What the MusicBrainz side of either port resolved. */
const musicBrainzFields = {
  title: v.optional(v.string()),
  artist: v.optional(v.string()),
  recordingId: v.optional(v.string()),
  workId: v.optional(v.string()),
  isrc: v.optional(v.string()),
};

/**
 * The enrichment step's report, as posted through the pipeline webhook. Each
 * port is null when it did not run or failed; `errors` says why so the
 * evidence survives. `lyricsKey` is the lyric text in R2 for alignment, never
 * shown, and `lyricsSource` says where the step found it.
 */
export const vEnrichmentResult = v.object({
  fingerprint: v.union(
    v.null(),
    v.object({ ...musicBrainzFields, score: v.number(), acoustId: v.optional(v.string()) }),
  ),
  text: v.union(
    v.null(),
    v.object({
      title: v.string(),
      artist: v.optional(v.string()),
      rationale: v.optional(v.string()),
      match: v.union(v.null(), v.object({ ...musicBrainzFields, score: v.number() })),
    }),
  ),
  lyricsKey: v.union(v.null(), v.string()),
  lyricsSource: v.optional(v.union(v.null(), vFetchedLyrics)),
  errors: v.optional(v.record(v.string(), v.string())),
});

export type EnrichmentResult = Infer<typeof vEnrichmentResult>;

export interface Typed {
  title: string;
  artist?: string;
}

export type SongIdentity = Pick<
  Doc<"songs">,
  "title" | "artist" | "normalisedKey" | "workId" | "recordingId" | "isrc" | "identifiedBy" | "confidence" | "evidence"
>;

/** Scores arrive from the signed Modal webhook, never a client, but a port
 * that misreports must still land inside the 0 to 1 the schema promises. */
function asConfidence(score: number): number {
  return Number.isFinite(score) ? Math.min(1, Math.max(0, score)) : 0;
}

/** Only the ports' fields that are set, so a weaker source never blanks a
 * stronger one's id with undefined. */
function present<T extends object>(fields: T): Partial<T> {
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined)) as Partial<T>;
}

/**
 * The song a rendition is, from what the uploader typed and what the two
 * identify ports found. Fingerprint wins outright at FINGERPRINT_ACCEPT; a
 * recording found by searching the cleaned text is next; the cleaned text
 * alone is next; the typed text is the floor an enrichment failure lands on.
 */
export function resolveIdentity(typed: Typed, result: EnrichmentResult | null): SongIdentity {
  const evidence = { typedTitle: typed.title, typedArtist: typed.artist };
  const fingerprint = result?.fingerprint;
  if (fingerprint && asConfidence(fingerprint.score) >= FINGERPRINT_ACCEPT && fingerprint.recordingId) {
    const title = fingerprint.title ?? result.text?.title ?? typed.title;
    const artist = fingerprint.artist ?? result.text?.artist ?? typed.artist;
    return {
      title,
      artist,
      normalisedKey: normalisedSongKey(artist, title),
      ...present({ recordingId: fingerprint.recordingId, workId: fingerprint.workId, isrc: fingerprint.isrc }),
      identifiedBy: "fingerprint",
      confidence: asConfidence(fingerprint.score),
      evidence: { ...evidence, fingerprintScore: fingerprint.score, acoustId: fingerprint.acoustId },
    };
  }
  const text = result?.text;
  if (text?.match?.recordingId) {
    const title = text.match.title ?? text.title;
    const artist = text.match.artist ?? text.artist;
    return {
      title,
      artist,
      normalisedKey: normalisedSongKey(artist, title),
      ...present({ recordingId: text.match.recordingId, workId: text.match.workId, isrc: text.match.isrc }),
      identifiedBy: "mbid",
      confidence: Math.min(MBID_CAP, asConfidence(text.match.score)),
      evidence: { ...evidence, searchScore: text.match.score, modelRationale: text.rationale },
    };
  }
  if (text) {
    return {
      title: text.title,
      artist: text.artist,
      normalisedKey: normalisedSongKey(text.artist, text.title),
      identifiedBy: "text",
      confidence: TEXT_CONFIDENCE,
      evidence: { ...evidence, modelRationale: text.rationale },
    };
  }
  return typedIdentity(typed);
}

/** Whether a fresh identity may overwrite what a song row already says. A
 * song an uploader named by hand is never overwritten; otherwise a stronger
 * or equal confidence wins, so a re-run cannot downgrade a fingerprint to
 * text. Which song a track points at is a separate question, settled by the
 * track's own lock (songLockedByOwner). */
export function outranks(fresh: SongIdentity, existing: Doc<"songs">): boolean {
  if (existing.identifiedBy === "manual") return false;
  return fresh.confidence >= existing.confidence;
}
